/**
 * custom.single-client.js
 *
 * Wrapper unico pra todas as chamadas a Single API v3 (/apps/single/v3/*).
 * Responsabilidades:
 *   - cache in-memory com TTL 60s
 *   - dedupe de requests concorrentes pra mesma URL (Promise memoization)
 *   - retry exponencial em 5xx (max 3 tentativas)
 *   - evento global 'customsingle:error' quando uma call falha terminal
 *   - feature flag window.URS.singleClient.enabled pra desligar em debug
 *
 * Compromisso critico: TODAS chamadas v3 do tema custom passam por aqui.
 * Quando Single mudar v3 -> v4, muda-se SO este arquivo (blast radius minimo).
 *
 * Uso:
 *   await window.URS.singleClient.get('/fan-subscription/manage');
 *   await window.URS.singleClient.post('/content/perks', {});
 */

(function () {
  'use strict';

  var BASE = '/apps/single/v3';
  var TTL_MS = 60 * 1000;
  var MAX_RETRIES = 3;

  var cache = new Map();
  var inflight = new Map();

  function cacheKey(method, path, body) {
    return method + ':' + path + (body ? ':' + JSON.stringify(body) : '');
  }

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  async function requestWithRetry(method, path, body, attempt) {
    attempt = attempt || 0;
    var url = BASE + path;
    var init = {
      method: method,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    };
    if (body !== undefined && method !== 'GET') {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    var res;
    try {
      res = await fetch(url, init);
    } catch (networkErr) {
      if (attempt < MAX_RETRIES - 1) {
        await sleep(Math.pow(2, attempt) * 300);
        return requestWithRetry(method, path, body, attempt + 1);
      }
      throw networkErr;
    }

    if (res.status >= 500 && attempt < MAX_RETRIES - 1) {
      await sleep(Math.pow(2, attempt) * 300);
      return requestWithRetry(method, path, body, attempt + 1);
    }

    if (!res.ok) {
      var err = new Error('v3 request failed: ' + res.status + ' ' + path);
      err.status = res.status;
      err.path = path;
      throw err;
    }

    var contentType = res.headers.get('content-type') || '';
    if (contentType.indexOf('application/json') !== -1) {
      return res.json();
    }
    return res.text();
  }

  function dispatchError(err) {
    try {
      document.dispatchEvent(new CustomEvent('customsingle:error', {
        detail: { path: err.path, status: err.status, message: err.message }
      }));
    } catch (e) { /* no-op */ }
  }

  async function get(path) {
    if (!client.enabled) throw new Error('singleClient disabled');
    var key = cacheKey('GET', path);
    var cached = cache.get(key);
    if (cached && Date.now() - cached.at < TTL_MS) return cached.data;
    if (inflight.has(key)) return inflight.get(key);

    var p = requestWithRetry('GET', path)
      .then(function (data) {
        cache.set(key, { data: data, at: Date.now() });
        inflight.delete(key);
        return data;
      })
      .catch(function (err) {
        inflight.delete(key);
        dispatchError(err);
        throw err;
      });
    inflight.set(key, p);
    return p;
  }

  async function post(path, body) {
    if (!client.enabled) throw new Error('singleClient disabled');
    try {
      return await requestWithRetry('POST', path, body || {});
    } catch (err) {
      dispatchError(err);
      throw err;
    }
  }

  async function cartAdd(variantId, quantity) {
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    });
    if (!res.ok) {
      var err = new Error('cart/add failed: ' + res.status);
      err.status = res.status;
      throw err;
    }
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    return res.json();
  }

  function invalidateCache(pathPrefix) {
    if (!pathPrefix) { cache.clear(); return; }
    cache.forEach(function (_, key) {
      if (key.indexOf(':' + pathPrefix) !== -1) cache.delete(key);
    });
  }

  var client = {
    enabled: true,
    get: get,
    post: post,
    cartAdd: cartAdd,
    invalidateCache: invalidateCache,
    _cache: cache,
    _inflight: inflight
  };

  window.URS = window.URS || {};
  window.URS.singleClient = client;
})();
